import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, Lock, ArrowRight, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/users/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error("Une erreur est survenue. Réessayez dans quelques instants.");
    } finally { setSubmitting(false); }
  };

  return (
    <AuthShell>
      {sent ? (
        <div className="text-center" data-testid="forgot-password-sent">
          <CheckCircle2 size={40} className="text-up mx-auto mb-4" />
          <h1 className="font-heading font-bold text-2xl mb-3">Vérifiez votre boîte mail</h1>
          <p className="text-white/60 text-sm mb-6">
            Si un compte existe avec l'adresse <b className="text-white">{email}</b>, vous recevrez
            un lien de réinitialisation dans quelques minutes (valable 1 heure). Pensez à vérifier vos spams.
          </p>
          <Link to="/login" className="btn btn-primary justify-center w-full py-3" data-testid="forgot-back-login">
            Retour à la connexion
          </Link>
        </div>
      ) : (
        <>
          <h1 className="font-heading font-bold text-2xl mb-2">Mot de passe oublié</h1>
          <p className="text-white/60 text-sm mb-6">
            Entrez l'adresse email de votre compte — nous vous enverrons un lien de réinitialisation.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label"><Mail size={12} className="inline mr-1" /> Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                     className="field w-full" placeholder="vous@example.com" data-testid="forgot-email-input" />
            </div>
            <button type="submit" disabled={submitting}
                    className="btn btn-primary w-full justify-center py-3 disabled:opacity-40"
                    data-testid="forgot-submit-btn">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : "Envoyer le lien"}
              <ArrowRight size={16} />
            </button>
          </form>
          <p className="mt-4 text-center text-sm text-white/50">
            <Link to="/login" className="text-accent-blue hover:underline" data-testid="forgot-login-link">Retour à la connexion</Link>
          </p>
        </>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Les deux mots de passe ne correspondent pas."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/users/reset-password`, { token, new_password: pw });
      toast.success("Mot de passe réinitialisé ! Connectez-vous.");
      navigate("/login");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Lien invalide ou expiré.");
    } finally { setSubmitting(false); }
  };

  if (!token) {
    return (
      <AuthShell>
        <div className="text-center" data-testid="reset-no-token">
          <h1 className="font-heading font-bold text-2xl mb-3">Lien invalide</h1>
          <p className="text-white/60 text-sm mb-6">Ce lien de réinitialisation est incomplet ou expiré.</p>
          <Link to="/forgot-password" className="btn btn-primary justify-center w-full py-3">Refaire une demande</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="font-heading font-bold text-2xl mb-2">Nouveau mot de passe</h1>
      <p className="text-white/60 text-sm mb-6">Choisissez un nouveau mot de passe (8 caractères minimum).</p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="label"><Lock size={12} className="inline mr-1" /> Nouveau mot de passe</label>
          <input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)}
                 className="field w-full" placeholder="Minimum 8 caractères" data-testid="reset-password-input" />
        </div>
        <div>
          <label className="label"><Lock size={12} className="inline mr-1" /> Confirmez le mot de passe</label>
          <input type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)}
                 className="field w-full" placeholder="Retapez le mot de passe" data-testid="reset-password-confirm-input" />
        </div>
        <button type="submit" disabled={submitting}
                className="btn btn-primary w-full justify-center py-3 disabled:opacity-40"
                data-testid="reset-submit-btn">
          {submitting ? <Loader2 size={16} className="animate-spin" /> : "Réinitialiser le mot de passe"}
          <ArrowRight size={16} />
        </button>
      </form>
    </AuthShell>
  );
}

function AuthShell({ children }) {
  return (
    <div className="min-h-screen bg-bg text-white flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-blue to-blue-600 flex items-center justify-center font-bold text-xl">A</div>
          <div className="font-heading font-bold text-xl">ANATRADEX</div>
        </Link>
        <div className="panel p-8">{children}</div>
      </div>
    </div>
  );
}
